import { useAppConstantsContext } from '@/context/AppConstants';
import { DataCard, openReportInNewTab } from '@/reports/index';
import {
  build as buildDataCardBadge,
  DataCardBadgeTruthinessMapping,
} from '@/reports/shared/DataCard/DataCardBadge/DataCardBadge';
import { commonTypes, peopleTypes } from '@/reports/types/index';
import {
  DataList,
  Text,
  Card,
  Box,
  Stack,
  Button,
  Link,
} from '@/theme/components';
import { capitalizeString, nameize, DateUtil } from '@/utils/index';
import { TwitterTimelineWidget } from './TwitterTimelineWidget';
import { convertTypeToString } from './convertTypeToString';

interface UsernameCardProps {
  username: commonTypes.Usernames[number];
}

export const UsernameCard: React.FC<UsernameCardProps> = ({ username }) => {
  const {
    config: { enabledReportTypes = [] },
    links: { baseUrl },
  } = useAppConstantsContext();

  let isUsernameSearchEnabled = true;
  if (enabledReportTypes.length > 0) {
    isUsernameSearchEnabled = enabledReportTypes.includes('username');
  }

  return (
    <Card sx={{ padding: 2.5 }} variant="outlined">
      <Stack
        spacing={2}
        direction={{ xs: 'column', md: 'row' }}
        alignItems="center"
      >
        <Box width={{ xs: '100%', md: '70%' }}>
          <Text variant="h2">
            {isUsernameSearchEnabled ? (
              <Button
                color="success"
                onClick={() =>
                  openReportInNewTab(
                    {
                      username: username.username || '',
                      searchType: 'username',
                    },
                    baseUrl
                  )
                }
              >
                {username.username}
              </Button>
            ) : (
              username.username
            )}
          </Text>
          <DataList
            items={[
              [
                'First seen',
                new DateUtil().parseDateFromString(
                  username.meta?.first_seen_date?.full || '',
                  'LLL yyyy'
                ) || null,
              ],
            ]}
            labelStyle={{ fontWeight: 'normal' }}
            colSpacing={0.5}
          />
        </Box>
        {isUsernameSearchEnabled && (
          <Box width={{ xs: '100%', md: '30%' }}>
            <Button
              color="success"
              variant="contained"
              sx={{
                whiteSpace: 'nowrap',
              }}
              fullWidth
              onClick={() => {
                if (username.username) {
                  openReportInNewTab(
                    {
                      username: username.username,
                      searchType: 'username',
                    },
                    baseUrl
                  );
                }
              }}
            >
              Search Username
            </Button>
          </Box>
        )}
      </Stack>
    </Card>
  );
};

interface ProfileCardProps {
  profile: peopleTypes.Profiles[number];
  thumbRating: () => JSX.Element;
  ownerName?: string;
}

export const ProfileCard: React.FC<ProfileCardProps> = ({
  profile,
  thumbRating,
  ownerName,
}) => {
  const Site = capitalizeString(profile.site);

  const badge = () => {
    const {
      config: { higherConfidenceThreshold },
    } = useAppConstantsContext();
    const dataConfidence = profile?.meta?.confidence || 0;
    const owner = nameize(ownerName ?? '');

    const badgeMapping: DataCardBadgeTruthinessMapping[] = [
      {
        name: 'bestMatch',
        truthiness: profile?.meta?.is_best || false,
        insert: 'Profile',
      },
      {
        name: 'higherConfidence',
        truthiness: dataConfidence >= higherConfidenceThreshold,
        insert: owner,
      },
    ];

    return buildDataCardBadge(badgeMapping);
  };

  const handleClick = (url: string | null) => {
    if (!url) return;
    window.open(url, '_blank');
  };
  return (
    <DataCard
      badge={badge()}
      thumbRating={thumbRating}
      titleProps={{ title: Site || 'Other Website' }}
      buttonProps={{
        label: Site && Site.length < 10 ? `Visit ${Site}` : 'Visit Site',
        onClick: () => handleClick(profile.url),
      }}
      cardStyles={{ mb: 0 }}
    >
      <DataList items={[['Type', convertTypeToString(profile.category)]]} />
      <Text sx={{ wordBreak: 'break-all', fontWeight: 'bold' }}>
        <Link
          href={profile.url ? profile.url : ''}
          target="_blank"
          color="success.main"
        >
          {profile.url?.replace(/^https?:\/\//, '')}
        </Link>
      </Text>
      <TwitterTimelineWidget url={profile.url || ''} />
    </DataCard>
  );
};
